<script setup lang="ts">
/**
 * SubmissionCard — mirrors .submission-card in resident-dashboard.html.
 * Shows issue details + StatusTimeline. Severity badge uses fixed color map.
 * All IDs and times use font-mono tabular-nums per CLAUDE.md.
 */
import StatusTimeline from './StatusTimeline.vue';
import BaseBadge from '@/components/ui/BaseBadge.vue';
import type { ResidentIssueListItemDto } from '@/composables/useResident';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
dayjs.extend(relativeTime);

defineProps<{
  issue: ResidentIssueListItemDto;
}>();

// Severity badge variant (brief §4 fixed mapping) — available for future use
type BadgeVariant = 'critical' | 'high' | 'medium' | 'low' | 'resolved' | 'progress' | 'info' | 'soft';
function statusVariant(s: string): BadgeVariant {
  const v = (s ?? '').toLowerCase();
  if (v === 'resolved' || v === '3') return 'resolved';
  if (v === 'inprogress' || v === 'in_progress' || v === '2') return 'progress';
  return 'info';
}
function statusLabel(s: string): string {
  const v = (s ?? '').toLowerCase();
  if (v === 'resolved' || v === '3') return 'Resolved';
  if (v === 'inprogress' || v === 'in_progress' || v === '2') return 'In progress';
  if (v === 'acknowledged' || v === '1') return 'Acknowledged';
  return 'Submitted';
}
function relTime(d: string | null | undefined): string {
  if (!d) return '';
  return dayjs(d).fromNow();
}
function shortId(id: string): string {
  return '#' + id.slice(0, 8).toUpperCase();
}
</script>

<template>
  <div class="submission-card" data-testid="submission-card">
    <!-- Header row: status badge, category, issue ID, submitted time -->
    <div class="submission-head">
      <BaseBadge :variant="statusVariant(issue.status)">
        {{ statusLabel(issue.status) }}
      </BaseBadge>
      <span class="submission-cat">{{ issue.category }}</span>
      <span class="id font-mono tabular-nums">{{ shortId(issue.id) }}</span>
      <span class="submitted font-mono tabular-nums">{{ relTime(issue.createdAt) }}</span>
    </div>

    <!-- Title -->
    <div class="submission-title">{{ issue.title || issue.description }}</div>

    <!-- Summary / description excerpt -->
    <div v-if="issue.description && issue.title" class="submission-summary">
      "{{ issue.description }}"
    </div>

    <!-- Status mini-timeline (not for resolved — simplified) -->
    <StatusTimeline
      :status="issue.status"
      :submitted-at="issue.createdAt"
      :acknowledged-at="issue.acknowledgedAt"
      :resolved-at="issue.resolvedAt"
    />
  </div>
</template>

<style scoped>
.submission-card {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--r-lg, 12px);
  padding: 18px 20px;
  margin-bottom: 12px;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.submission-card:hover { border-color: var(--border-strong); box-shadow: var(--shadow-sm); }
.submission-head {
  display: flex;
  gap: 10px;
  align-items: center;
  margin-bottom: 8px;
  flex-wrap: wrap;
}
.submission-head .id { font-size: 11px; color: var(--muted-2); font-weight: 600; }
.submission-cat {
  font-size: 11.5px;
  background: var(--bg-soft);
  border: 1px solid var(--border);
  padding: 2px 8px;
  border-radius: 999px;
  font-weight: 500;
  color: var(--fg-soft);
}
.submission-head .submitted { margin-left: auto; font-size: 11.5px; color: var(--muted); }
.submission-title { font-size: 14.5px; font-weight: 600; margin-bottom: 6px; line-height: 1.4; }
.submission-summary { font-size: 13px; color: var(--muted); line-height: 1.5; margin-bottom: 14px; }
</style>
